import { Input } from 'styles/Styles.styled';
import { useField, useFormikContext } from 'formik';
import SmartLabel from 'components/v2/labels/SmartLabel';
import { TooltipPosition } from 'components/controls/Tooltip';
import { useTheme } from 'styled-components';
import { Theme } from 'styles/theme';
import { useEffect } from 'react';
import { toaster } from 'components/v2/toast';
import { StyledInputErrorMessage, StyledInputWrapper } from './styled';
import { NormalizeSpaces } from 'util/utils';

type Props = {
  name: string;
  id?: string;
  type?: string;
  label?: string;
  tooltip?: string;
  tooltipPosition?: TooltipPosition;
  enableTooltipError?: boolean;
  supressErrors?: boolean;
  trim?: boolean;
  errorMessageStyle?: React.CSSProperties;
  renderErrorMessage?: (error: string) => React.ReactNode;
  shouldShowTooltipError?: (error: string) => boolean;
  shouldShowBottomError?: (error: string) => boolean;
  shouldHighlightBorder?: (error: string) => boolean;
  showErrorsOnSubmit?: boolean;
  [key: string]: any;
};

const TextInput = ({ label, tooltip, tooltipPosition, enableTooltipError, showErrorsOnSubmit, trim, ...props }: Props) => {
  const theme = useTheme() as Theme;
  const [field, meta, helpers] = useField(props);
  const { submitCount } = useFormikContext();

  const applyTrim = (value: string) => {
    const normalized = NormalizeSpaces(value);
    if (normalized !== value) {
      helpers.setValue(normalized);
      toaster({
        severity: 'yellow-exclamation-warning',
        summary: 'Extra spaces have been removed.',
        details: 'Input fields cannot contain leading, trailing or double spaces.',
      });
    }
  };

  useEffect(() => {
    if (trim && !props.disabled && submitCount > 0 && typeof field.value === 'string') {
      applyTrim(field.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]);

  const isErrorActive = (!!showErrorsOnSubmit || meta.touched) && !props.supressErrors;

  const rawError = isErrorActive ? (meta.error as string | undefined) : undefined;

  const isTooltipAllowed = props.shouldShowTooltipError ? (rawError ? props.shouldShowTooltipError(rawError) : false) : true;
  const tooltipError = enableTooltipError && meta.touched && rawError && isTooltipAllowed ? rawError : undefined;

  const isHighlightAllowed = props.shouldHighlightBorder ? !!rawError && props.shouldHighlightBorder(rawError) : false;
  const isBottomAllowed = props.shouldShowBottomError ? !!rawError && props.shouldShowBottomError(rawError) : true;

  const mergedStyle = {
    width: '100%',
    ...(props.style ?? {}),
    borderColor: rawError && isHighlightAllowed ? theme.colors.systemStatusError : props.style?.borderColor,
    marginBottom: 0,
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (trim && !props.disabled && typeof field.value === 'string') {
      applyTrim(field.value);
    }
    field.onBlur(e);
  };

  return (
    <>
      <SmartLabel
        htmlFor={props.id || props.name}
        label={label}
        tooltip={tooltip}
        tooltipPosition={tooltipPosition}
        labelStyle={{ fontFamily: `${theme.fontFamilies.body}`, fontSize: `${theme.fontSizes.small}rem` }}
        boxStyle={{ marginBottom: 0 }}
        required={props.required}
        hasValue={enableTooltipError ? !!field.value && field.value !== '' : true}
        error={tooltipError}
      />
      <StyledInputWrapper {...props}>
        <Input {...field} {...props} style={mergedStyle} onBlur={handleBlur} />
        {meta.touched && rawError && !props.supressErrors && isBottomAllowed ? (
          <StyledInputErrorMessage>
            {props.renderErrorMessage ? props.renderErrorMessage(rawError) : rawError}
          </StyledInputErrorMessage>
        ) : null}
      </StyledInputWrapper>
    </>
  );
};

export default TextInput;
